import React, { useEffect, useState, useMemo } from "react";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import { getVault, claimMilestone, getStatus, claimRecoveryPack } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Gem, Lock, Gift, Sparkles, Trophy, Crown, Check, Headphones,
  Watch, Package, Ticket, AlertTriangle, Zap, Flame, TrendingUp,
} from "lucide-react";
import confetti from "canvas-confetti";

const ICON_BY_ID = {
  cm_150k: Ticket,
  cm_250k: Gift,
  cm_500k: Headphones,
  cm_1m: Package,
  cm_1_8m: Watch,
  cm_3m: Crown,
  cm_5m: Package,
  cm_8m: Sparkles,
  cm_12m: Trophy,
};

const COLOR_BY_ID = {
  cm_150k: "#00FF66",
  cm_250k: "#00F0FF",
  cm_500k: "#B026FF",
  cm_1m: "#FFD600",
  cm_1_8m: "#FF66CC",
  cm_3m: "#FF0055",
  cm_5m: "#00F0FF",
  cm_8m: "#B026FF",
  cm_12m: "#FFD600",
};

export default function Vault() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [claiming, setClaiming] = useState(null);

  const load = async () => {
    const [v, s] = await Promise.all([getVault(), getStatus()]);
    setData(v);
    setStatus(s);
  };

  useEffect(() => { load(); }, []);

  const onClaim = async (m) => {
    if (claiming) return;
    setClaiming(m.id);
    try {
      await claimMilestone(m.id);
      confetti({ particleCount: 160, spread: 100, origin: { y: 0.4 }, colors: ["#FFD600", "#00FF66", "#00F0FF"] });
      toast.success(`${m.name} claimed!`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim");
    } finally { setClaiming(null); }
  };

  const onRecovery = async () => {
    try {
      const r = await claimRecoveryPack();
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.4 }, colors: ["#00FF66", "#00F0FF"] });
      toast.success(`Recovery Pack claimed · +${r.credits_added.toLocaleString("en-IN")} Credits`);
      await load();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim Recovery Pack");
    }
  };

  const credits = data?.fan_credits ?? 0;
  const best = data?.best_wallet ?? 0;
  const nxt = data?.next_milestone;
  const progress = Math.min(100, data?.progress_pct || 0);
  const growth = data?.growth_from_start ?? 0;
  const milestones = data?.milestones || [];
  const recovery = status?.recovery;

  const unlockedCount = useMemo(() => milestones.filter(m => m.unlocked).length, [milestones]);

  if (!data) return (
    <div className="min-h-screen" data-testid="vault-page">
      <Header title="Reward Vault" />
      <div className="px-4 pt-6 text-white/60">Loading Vault…</div>
      <BottomNav />
    </div>
  );

  return (
    <div className="min-h-screen pb-safe" data-testid="vault-page">
      <Header title="Reward Vault" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-4">

        {/* Wallet hero */}
        <section className="glass-strong rounded-3xl p-5 relative overflow-hidden" data-testid="vault-wallet-card">
          <div aria-hidden className="pointer-events-none absolute -top-12 -right-12 w-56 h-56 rounded-full opacity-40"
            style={{ background: "radial-gradient(circle, #B026FF, transparent 70%)" }} />
          <div className="flex items-center gap-2 relative z-10">
            <Gem className="w-4 h-4 text-[#FFD600]" />
            <p className="text-xs uppercase tracking-[0.3em] font-bold text-white/50">Fanpuff Credits</p>
          </div>
          <p className="font-display text-4xl font-bold mt-1 font-mono-brand" data-testid="vault-credits">
            {credits.toLocaleString("en-IN")}
          </p>
          <p className={`text-xs mt-1 ${growth >= 0 ? "text-[#00FF66]" : "text-[#FF0055]"}`} data-testid="vault-growth">
            <TrendingUp className="inline w-3 h-3 mr-1" />
            {growth >= 0 ? "+" : ""}{growth.toLocaleString("en-IN")} from starting 1,00,000
          </p>

          {nxt && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-white/60">Next: <b className="text-white">{nxt.name}</b> at {nxt.credits.toLocaleString("en-IN")}</span>
                <span className="text-white/50">{progress.toFixed(1)}%</span>
              </div>
              <div className="h-2 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full" style={{ width: `${progress}%`, background: "linear-gradient(90deg,#FF0055,#B026FF,#00F0FF)" }} />
              </div>
              <p className="text-[10px] text-white/45 mt-1">
                {(nxt.credits - credits).toLocaleString("en-IN")} Fanpuff Credits to unlock
              </p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 mt-4">
            <StatTile label="Best ever" value={best.toLocaleString("en-IN")} />
            <StatTile label="Tiers unlocked" value={`${unlockedCount} / ${milestones.length}`} />
            <StatTile label="Claims" value={(data.claims?.length || 0).toString()} />
          </div>
        </section>

        {/* Recovery Pack */}
        {recovery && (recovery.eligible || recovery.cooldown_active || recovery.tax_remaining > 0) && (
          <section className="glass rounded-2xl p-4" data-testid="recovery-card">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#FFD600]" />
              <p className="text-xs uppercase tracking-widest font-bold text-[#FFD600]">Recovery Pack</p>
            </div>
            {recovery.eligible ? (
              <>
                <p className="font-display text-lg font-bold mt-1">Wallet low? Bounce back.</p>
                <p className="text-xs text-white/60 mt-1">
                  Restore your wallet to 40,000 Fanpuff Credits. Free. Once per {status?.recovery?.cooldown_remaining_days || 7} days.
                  A {recovery.tax_rate_pct}% recovery tax applies to your next 20,000 winnings.
                </p>
                <button
                  onClick={onRecovery}
                  data-testid="recovery-claim-btn"
                  className="mt-3 w-full py-2.5 rounded-full font-bold text-black"
                  style={{ background: "linear-gradient(90deg,#FFD600,#00FF66)" }}
                >
                  Claim Recovery Pack
                </button>
              </>
            ) : recovery.cooldown_active ? (
              <p className="text-xs text-white/60 mt-1">
                Recovery Pack used recently. Available again in {recovery.cooldown_remaining_days} day(s).
              </p>
            ) : recovery.tax_remaining > 0 ? (
              <p className="text-xs text-white/60 mt-1">
                Recovery tax active — {recovery.tax_rate_pct}% tax on your next{" "}
                <b className="text-white">{recovery.tax_remaining.toLocaleString("en-IN")}</b> Fanpuff Credits of winnings.
              </p>
            ) : null}
          </section>
        )}

        {/* Milestone ladder */}
        <section>
          <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-3">
            <Gift className="w-5 h-5 text-[#FFD600]" /> Reward Ladder
          </h2>
          <p className="text-xs text-white/55 mb-3">
            Grow your Fanpuff Credits to unlock real rewards — vouchers, earbuds, smartwatches, even an iPhone or Smart TV. Premium tiers (3M+) are sponsor-backed with limited monthly inventory.
          </p>
          <div className="space-y-3">
            {milestones.map((m) => {
              const Icon = ICON_BY_ID[m.id] || Gem;
              const color = COLOR_BY_ID[m.id] || "#B026FF";
              const unlocked = m.unlocked;
              const claimed = m.claimed;
              const paused = m.tier_enabled === false;
              const oos = m.stock <= 0;
              const isPremium = !!m.premium;
              return (
                <div
                  key={m.id}
                  className={`glass rounded-3xl overflow-hidden relative ${unlocked ? "" : "opacity-80"}`}
                  style={{ border: unlocked ? `1px solid ${color}55` : "1px solid rgba(255,255,255,0.08)" }}
                  data-testid={`milestone-${m.id}`}
                >
                  {/* Reward photo */}
                  {m.image_url && (
                    <div className="relative h-32 overflow-hidden">
                      <img
                        src={m.image_url}
                        alt={m.hero_reward || m.name}
                        className={`w-full h-full object-cover ${unlocked ? "" : "grayscale"}`}
                        loading="lazy"
                        data-testid={`milestone-img-${m.id}`}
                      />
                      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 25%, rgba(5,5,8,0.85)), linear-gradient(120deg, ${color}22, transparent 60%)` }} />
                      <div className="absolute top-3 left-3 flex items-center gap-1.5">
                        <span className="chip text-[10px]" style={{ background: `${color}22`, color, border: `1px solid ${color}66` }}>
                          {m.tier}
                        </span>
                        {isPremium && (
                          <span className="chip text-[10px]" style={{ background: "rgba(255,214,0,0.18)", color: "#FFD600", border: "1px solid rgba(255,214,0,0.5)" }}>
                            <Sparkles className="w-2.5 h-2.5" /> Sponsor
                          </span>
                        )}
                      </div>
                      {!unlocked && (
                        <div className="absolute top-3 right-3">
                          <span className="chip text-[10px]" style={{ background: "rgba(0,0,0,0.55)", color: "#fff", border: "1px solid rgba(255,255,255,0.18)" }}>
                            <Lock className="w-2.5 h-2.5" /> Locked
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-2.5 left-3 right-3">
                        <p className="font-display text-base font-bold leading-tight drop-shadow">{m.hero_reward || m.name}</p>
                      </div>
                    </div>
                  )}

                  <div aria-hidden className="pointer-events-none absolute -bottom-12 -right-12 w-40 h-40 rounded-full opacity-20"
                    style={{ background: `radial-gradient(circle, ${color}, transparent 70%)` }} />
                  <div className="p-4 flex items-start gap-3 relative z-10">
                    <span
                      className="w-11 h-11 rounded-2xl grid place-items-center shrink-0"
                      style={{
                        background: unlocked ? `${color}33` : "rgba(255,255,255,0.05)",
                        border: `1px solid ${unlocked ? `${color}88` : "rgba(255,255,255,0.1)"}`,
                      }}
                    >
                      {unlocked ? <Icon className="w-5 h-5" style={{ color }} /> : <Lock className="w-4 h-4 text-white/40" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-bold">{m.name}</p>
                      </div>
                      <p className="text-[11px] text-white/55 font-mono-brand">
                        {m.credits.toLocaleString("en-IN")} Fanpuff Credits
                        {m.value_inr ? ` · ${m.value_inr} value` : ""}
                      </p>
                      <ul className="mt-2 space-y-0.5 text-xs text-white/70">
                        {m.rewards.slice(0, 3).map((r, i) => <li key={`${m.id}-reward-${i}`}>• {r}</li>)}
                        {m.rewards.length > 3 && <li className="text-white/40">+ {m.rewards.length - 3} more options</li>}
                      </ul>
                      <div className="mt-3">
                        {claimed ? (
                          <span className="chip" style={{ background: "#00FF6622", color: "#00FF66", border: "1px solid #00FF6655" }}>
                            <Check className="w-3 h-3" /> Claimed
                          </span>
                        ) : paused ? (
                          <span className="chip" style={{ background: "#FFD60022", color: "#FFD600", border: "1px solid #FFD60055" }}>
                            <Lock className="w-3 h-3" /> Tier paused — back soon
                          </span>
                        ) : oos ? (
                          <span className="chip" style={{ background: "#FF005522", color: "#FF0055", border: "1px solid #FF005555" }}>
                            Out of stock — refreshes monthly
                          </span>
                        ) : unlocked ? (
                          <button
                            onClick={() => onClaim(m)}
                            disabled={claiming === m.id}
                            data-testid={`claim-${m.id}`}
                            className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-bold text-black"
                            style={{ background: `linear-gradient(90deg,${color},#fff)` }}
                          >
                            {claiming === m.id ? "Claiming…" : "Claim reward"}
                          </button>
                        ) : (
                          <p className="text-[11px] text-white/45">
                            <Lock className="inline w-3 h-3 mr-1" />
                            {(m.credits - credits).toLocaleString("en-IN")} more credits to unlock
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Claim history */}
        {data.claims?.length > 0 && (
          <section className="glass rounded-2xl p-4" data-testid="claim-history">
            <h3 className="font-display text-sm font-bold flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#FFD600]" /> Claim history
            </h3>
            <ul className="mt-2 space-y-1 text-xs text-white/70">
              {data.claims.slice(0, 6).map((c) => (
                <li key={c.claim_id} className="flex items-center justify-between">
                  <span className="truncate">{c.milestone_name || c.milestone_id}</span>
                  <span className="text-white/45 font-mono-brand">{new Date(c.claimed_at).toLocaleDateString("en-IN")}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Legal */}
        <p className="text-[10px] text-white/35 leading-relaxed py-4">
          Fanpuff is a free-to-play entertainment fantasy experience. Fanpuff Credits have no cash value and cannot be withdrawn.
          No deposits or real-money gaming are involved. Rewards are promotional and subject to eligibility, availability, and platform rules.
        </p>

        <div className="h-10" />
      </div>
      <BottomNav />
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="glass rounded-xl p-2.5 text-center">
      <p className="text-[9px] uppercase tracking-widest text-white/45 font-bold">{label}</p>
      <p className="font-display text-sm font-semibold mt-0.5 truncate">{value}</p>
    </div>
  );
}
